"use client";

import * as React from "react";

import { FUNDING_ELIGIBILITY_ESTIMATE_LOADING_MS } from "@/components/home/funding-eligibility-shared";
import type { FundingEligibilityStatus } from "@/components/home/funding-eligibility-shared";

export interface FundingEligibilityFormValues {
  revenue: string;
  category: string;
  /** Years trading — drives the < 2 years ineligibility gate (restaurant page). */
  yearsInBusiness: string;
  country: string;
}

interface FundingEligibilityState {
  status: FundingEligibilityStatus;
  ineligibleMessage: string | null;
  ineligibleRevenueDisplay: string | null;
}

interface FundingEligibilityContextValue extends FundingEligibilityState {
  formValues: FundingEligibilityFormValues;
  applicationEmail: string;
  isStepOneComplete: boolean;
  isEstimateLoading: boolean;
  /** True once the first eligible estimate has finished its loading reveal. */
  hasEstimateRevealed: boolean;
  confettiBurst: number;
  stepOneInteracted: boolean;
  stepTwoSubmitted: boolean;
  setEligibilityState: (state: FundingEligibilityState) => void;
  setFormValues: (values: FundingEligibilityFormValues) => void;
  setApplicationEmail: (email: string) => void;
  reloadEstimate: () => void;
  markStepOneInteracted: () => void;
  markStepTwoSubmitted: () => void;
}

const FundingEligibilityContext =
  React.createContext<FundingEligibilityContextValue | null>(null);

const EMPTY_FORM_VALUES: FundingEligibilityFormValues = {
  revenue: "",
  category: "",
  yearsInBusiness: "",
  country: "",
};

export function FundingEligibilityProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const [eligibilityStatus, setEligibilityStatus] =
    React.useState<FundingEligibilityStatus>("incomplete");
  const [ineligibleMessage, setIneligibleMessage] = React.useState<
    string | null
  >(null);
  const [ineligibleRevenueDisplay, setIneligibleRevenueDisplay] =
    React.useState<string | null>(null);
  const [formValues, setFormValuesState] =
    React.useState<FundingEligibilityFormValues>(EMPTY_FORM_VALUES);
  const [applicationEmail, setApplicationEmail] = React.useState("");
  const [isEstimateLoading, setEstimateLoading] = React.useState(false);
  const [hasEstimateRevealed, setHasEstimateRevealed] = React.useState(false);
  const [confettiBurst, setConfettiBurst] = React.useState(0);
  const [stepOneInteracted, setStepOneInteracted] = React.useState(false);
  const [stepTwoSubmitted, setStepTwoSubmitted] = React.useState(false);
  const hasCelebratedRef = React.useRef(false);
  const loadingTimeoutRef = React.useRef<ReturnType<typeof setTimeout> | null>(
    null
  );

  React.useEffect(() => {
    return () => {
      if (loadingTimeoutRef.current) {
        clearTimeout(loadingTimeoutRef.current);
      }
    };
  }, []);

  const setEligibilityState = React.useCallback(
    ({
      status,
      ineligibleMessage: nextIneligibleMessage,
      ineligibleRevenueDisplay: nextIneligibleRevenueDisplay,
    }: FundingEligibilityState) => {
      setEligibilityStatus(status);
      setIneligibleMessage(nextIneligibleMessage);
      setIneligibleRevenueDisplay(nextIneligibleRevenueDisplay);

      if (status === "eligible" && !hasCelebratedRef.current) {
        setEstimateLoading(true);

        if (loadingTimeoutRef.current) {
          clearTimeout(loadingTimeoutRef.current);
        }

        loadingTimeoutRef.current = setTimeout(() => {
          loadingTimeoutRef.current = null;
          hasCelebratedRef.current = true;
          setHasEstimateRevealed(true);
          setEstimateLoading(false);
          setConfettiBurst(1);
        }, FUNDING_ELIGIBILITY_ESTIMATE_LOADING_MS);

        return;
      }

      if (status !== "eligible") {
        if (loadingTimeoutRef.current && !hasCelebratedRef.current) {
          clearTimeout(loadingTimeoutRef.current);
          loadingTimeoutRef.current = null;
          setEstimateLoading(false);
        }
      }
    },
    []
  );

  const setFormValues = React.useCallback(
    (values: FundingEligibilityFormValues) => {
      setFormValuesState(values);
    },
    []
  );

  const reloadEstimate = React.useCallback(() => {
    if (eligibilityStatus !== "eligible") return;

    setEstimateLoading(true);

    if (loadingTimeoutRef.current) {
      clearTimeout(loadingTimeoutRef.current);
    }

    loadingTimeoutRef.current = setTimeout(() => {
      loadingTimeoutRef.current = null;
      setEstimateLoading(false);
    }, FUNDING_ELIGIBILITY_ESTIMATE_LOADING_MS);
  }, [eligibilityStatus]);

  const isStepOneComplete = eligibilityStatus === "eligible";

  const markStepOneInteracted = React.useCallback(() => {
    setStepOneInteracted(true);
  }, []);

  const markStepTwoSubmitted = React.useCallback(() => {
    setStepTwoSubmitted(true);
  }, []);

  const value = React.useMemo(
    () => ({
      status: eligibilityStatus,
      ineligibleMessage,
      ineligibleRevenueDisplay,
      formValues,
      applicationEmail,
      isStepOneComplete,
      isEstimateLoading,
      hasEstimateRevealed,
      confettiBurst,
      stepOneInteracted,
      stepTwoSubmitted,
      setEligibilityState,
      setFormValues,
      setApplicationEmail,
      reloadEstimate,
      markStepOneInteracted,
      markStepTwoSubmitted,
    }),
    [
      eligibilityStatus,
      ineligibleMessage,
      ineligibleRevenueDisplay,
      formValues,
      applicationEmail,
      isStepOneComplete,
      isEstimateLoading,
      hasEstimateRevealed,
      confettiBurst,
      stepOneInteracted,
      stepTwoSubmitted,
      setEligibilityState,
      setFormValues,
      reloadEstimate,
      markStepOneInteracted,
      markStepTwoSubmitted,
    ]
  );

  return (
    <FundingEligibilityContext.Provider value={value}>
      {children}
    </FundingEligibilityContext.Provider>
  );
}

export function useFundingEligibility() {
  const context = React.useContext(FundingEligibilityContext);
  if (!context) {
    throw new Error(
      "useFundingEligibility must be used within FundingEligibilityProvider"
    );
  }
  return context;
}

/**
 * Step-one success UI unlocks after the first estimate finishes loading.
 * Stays visible during category-change reloads so sibling steps don't resize.
 */
export function useFundingEligibilitySuccessVisible() {
  const { status, isEstimateLoading, hasEstimateRevealed } =
    useFundingEligibility();
  return status === "eligible" && (hasEstimateRevealed || !isEstimateLoading);
}

export function useFundingEligibilityIneligibleVisible() {
  const { status } = useFundingEligibility();
  return status === "ineligible";
}

/** Step 02 email capture is active when eligible or ineligible (all fields filled). */
export function useFundingEligibilityStepTwoActive() {
  const { status } = useFundingEligibility();
  return status === "eligible" || status === "ineligible";
}

/** Gradient emphasis on step 02 once step one resolves, until step 02 is submitted. */
export function useFundingEligibilityStepTwoGradient() {
  const { stepTwoSubmitted } = useFundingEligibility();
  const showSuccess = useFundingEligibilitySuccessVisible();
  const showIneligible = useFundingEligibilityIneligibleVisible();
  return (showSuccess || showIneligible) && !stepTwoSubmitted;
}

/** Gradient emphasis on step 03 once step 02 email capture is submitted. */
export function useFundingEligibilityStepThreeGradient() {
  const { stepTwoSubmitted } = useFundingEligibility();
  return stepTwoSubmitted;
}

/** Gradient emphasis on step 01 after the user interacts, while step one is incomplete. */
export function useFundingEligibilityStepOneGradient() {
  const { stepOneInteracted, status } = useFundingEligibility();
  return stepOneInteracted && status === "incomplete";
}
